import { useMutation } from '@tanstack/react-query';

import { useUnsubscribeAnalytics } from '@features/Unsubscribe/hooks/use-unsubscribe-analytics';
import type { TpWizardValues } from '@features/Unsubscribe/types';
import { useGetAppName } from 'src/utils/hooks';
import { WIZARD_STEPS } from '../сonstants';

type UseTransferToLifetimeProps = {
  readonly query: {
    readonly email?: string | string[];
    readonly user_id?: string | string[];
  };
  readonly wizard: { readonly goToStep: (step: TpWizardValues) => void };
  readonly eventName: string;
  readonly options?: Record<string, unknown>;
  readonly successStep: TpWizardValues;
  readonly price_id?: string;
  readonly currency?: string;
};

export const useTransferToLifetime = ({
  query,
  wizard,
  eventName,
  options = {},
  successStep,
  price_id,
  currency,
}: UseTransferToLifetimeProps) => {
  const {
    defaultEventOptions,
    trackUnsubscribeEvent,
    trackUnsubscribeRequestError,
    trackUnsubscribeRequestSuccess,
  } = useUnsubscribeAnalytics({ email: query?.email, userId: query?.user_id });
  const app = useGetAppName();

  return useMutation({
    mutationFn: async () => {
      const formData = {
        app,
        email: defaultEventOptions.email,
        user_id: defaultEventOptions.userId,
        customer_id: defaultEventOptions.userId,
        price_id,
        currency,
      };

      const response = await fetch(
        `${process.env.NEXT_PUBLIC_API_MICROSERVICE_ENDPOINT}stripe/transfer_to_lifetime`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/x-www-form-urlencoded',
          },
          body: new URLSearchParams(formData as Record<string, string>),
        },
      );

      if (!response.ok) {
        throw new Error(`Transfer to lifetime failed: ${response.statusText}`, {
          cause: response,
        });
      }

      return response.status;
    },
    onSuccess: (statusCode) => {
      trackUnsubscribeRequestSuccess(
        'cancel_subscription_change_succeeded',
        {
          endpoint: 'stripe/transfer_to_lifetime',
          requestName: 'change_subscription',
        },
        statusCode,
      );

      trackUnsubscribeEvent(eventName, options);

      wizard.goToStep(successStep);
    },
    onError: (error) => {
      trackUnsubscribeRequestError('cancel_subscription_change_failed', error, {
        endpoint: 'stripe/transfer_to_lifetime',
        requestName: 'change_subscription',
      });

      console.error('Transfer to lifetime failed:', error);
      wizard.goToStep(WIZARD_STEPS.ERROR);
    },
  });
};
